import type { ShopifyProduct } from '@/lib/shopify/api';
import type { ShopifyCartItem } from '@/stores/shopifyCartStore';

export const GA4_ID = 'G-XXXXXXXXXX';
export const FB_PIXEL_ID = 'XXXXXXXXXXXXXXXXX';

/** Google Ads conversion tracking */
export const GOOGLE_ADS_ID = 'AW-18455976596';
export const GOOGLE_ADS_PURCHASE_LABEL = '-8gcCN7QnfocEJS1v-BE';
export const GOOGLE_ADS_ADD_TO_CART_LABEL = 'EzHOCOHQnfocEJS1v-BE';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

function isGA4Ready(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' && GA4_ID !== 'G-XXXXXXXXXX';
}

function isFBReady(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function' && FB_PIXEL_ID !== 'XXXXXXXXXXXXXXXXX';
}

function isAdsReady(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/** Send a Google Ads conversion. */
function trackAdsConversion(params: {
  label: string;
  value?: number;
  currency?: string;
  transactionId?: string;
}): void {
  if (!isAdsReady()) return;
  window.gtag!('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${params.label}`,
    value: params.value,
    currency: params.currency || 'USD',
    transaction_id: params.transactionId || '',
  });
}

function getLeadItem(product: ShopifyProduct['node']) {
  const variant = product.variants.edges[0]?.node;
  const price = parseFloat(
    variant?.price.amount || product.priceRange.minVariantPrice.amount || '0'
  );
  const currency =
    variant?.price.currencyCode || product.priceRange.minVariantPrice.currencyCode || 'USD';
  return { price, currency };
}

export function trackViewItem(product: ShopifyProduct['node']): void {
  if (!isGA4Ready() && !isFBReady()) return;
  const { price, currency } = getLeadItem(product);

  if (isGA4Ready()) {
    window.gtag!('event', 'view_item', {
      currency,
      value: price,
      items: [
        {
          item_id: product.handle,
          item_name: product.title,
          price,
          currency,
          item_brand: product.vendor || undefined,
        },
      ],
    });
  }

  if (isFBReady()) {
    window.fbq!('track', 'ViewContent', {
      content_ids: [product.handle],
      content_name: product.title,
      content_type: 'product',
      currency,
      value: price,
    });
  }
}

export function trackAddToCart(params: {
  product: ShopifyProduct['node'];
  quantity: number;
}): void {
  if (!isGA4Ready() && !isFBReady() && !isAdsReady()) return;
  const { product, quantity } = params;
  const { price, currency } = getLeadItem(product);

  trackAdsConversion({
    label: GOOGLE_ADS_ADD_TO_CART_LABEL,
    value: price * quantity,
    currency,
  });

  if (isGA4Ready()) {
    window.gtag!('event', 'add_to_cart', {
      currency,
      value: price * quantity,
      items: [
        {
          item_id: product.handle,
          item_name: product.title,
          price,
          quantity,
          currency,
          item_brand: product.vendor || undefined,
          item_list_name: lastInteractionSource,
        },
      ],
    });
  }


  if (isFBReady()) {
    window.fbq!('track', 'AddToCart', {
      content_ids: [product.handle],
      content_name: product.title,
      content_type: 'product',
      currency,
      value: price * quantity,
    });
  }
}

export function trackInitiateCheckout(items: ShopifyCartItem[], value: number, currency: string): void {
  if (!isGA4Ready() && !isFBReady()) return;

  const gaItems = items.map((item) => ({
    item_id: item.product.node.handle,
    item_name: item.product.node.title,
    price: parseFloat(item.price.amount),
    quantity: item.quantity,
    currency: item.price.currencyCode,
    item_brand: item.product.node.vendor || undefined,
  }));

  if (isGA4Ready()) {
    window.gtag!('event', 'begin_checkout', {
      currency,
      value,
      items: gaItems,
    });
  }

  if (isFBReady()) {
    window.fbq!('track', 'InitiateCheckout', {
      content_ids: items.map((i) => i.product.node.handle),
      content_type: 'product',
      currency,
      value,
    });
  }
}

export function trackPurchase(transactionId: string, value?: number, currency = 'USD'): void {
  trackAdsConversion({ label: GOOGLE_ADS_PURCHASE_LABEL, value, currency, transactionId });

  if (!isGA4Ready() && !isFBReady()) return;


  if (isGA4Ready()) {
    window.gtag!('event', 'purchase', {
      transaction_id: transactionId,
      value,
      currency,
    });
  }

  if (isFBReady()) {
    window.fbq!('track', 'Purchase', {
      value,
      currency,
      order_id: transactionId,
    });
  }
}

/* ------------------------------------------------------------------ *
 * Attribution: where the shopping interaction started (blog, catalog) *
 * ------------------------------------------------------------------ */

let lastInteractionSource = 'catalog';

export function setInteractionSource(source: string): void {
  lastInteractionSource = source;
}

export function getInteractionSource(): string {
  return lastInteractionSource;
}

/** GA4 view_promotion: an in-article product block became visible. */
export function trackViewPromotion(params: {
  promotionId: string;
  promotionName: string;
  itemIds: string[];
}): void {
  if (!isGA4Ready()) return;
  window.gtag!('event', 'view_promotion', {
    promotion_id: params.promotionId,
    promotion_name: params.promotionName,
    creative_slot: lastInteractionSource,
    items: params.itemIds.map((id) => ({ item_id: id })),
  });
}

/** GA4 select_promotion: a product inside an in-article block was clicked. */
export function trackSelectPromotion(params: {
  promotionId: string;
  promotionName: string;
  itemId: string;
  itemName: string;
}): void {
  if (!isGA4Ready()) return;
  window.gtag!('event', 'select_promotion', {
    promotion_id: params.promotionId,
    promotion_name: params.promotionName,
    creative_slot: lastInteractionSource,
    items: [{ item_id: params.itemId, item_name: params.itemName }],
  });
}

/** Email capture (deal alerts lead magnet). */
export function trackLeadSignup(source: string): void {
  if (isGA4Ready()) {
    window.gtag!('event', 'generate_lead', { method: source });
  }
  if (isFBReady()) {
    window.fbq!('track', 'Lead', { content_name: source });
  }
}
